import { onBeforeUnmount, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  type NavigationGuardWithThis,
  type RouteLocationNormalized,
} from 'vue-router'

export interface UnsavedGuardOptions {
  /**
   * The route params naming what the page shows — `assetId` for an asset's tab. The router reuses a
   * view when only a param changes (the next asset's same tab), which vue-router does not count as
   * leaving it, so no leave guard runs; a change to one of these params asks too. A change to the
   * query or to any other param does not. Another organization asks whatever the params, through
   * {@link createOrganizationChangeGuard}.
   */
  params?: readonly string[]
}

type Question = () => boolean | Promise<boolean>

/** The question of every form on screen that guards its changes, for the organization change guard. */
const questions = new Set<Question>()

/** Puts a form's question to every change of organization, until the returned call takes it back. */
export function askOnOrganizationChange(ask: Question): () => void {
  questions.add(ask)
  return () => {
    questions.delete(ask)
  }
}

/** Whether a navigation enters an organization other than the one the page is in. */
export function entersOtherOrganization(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
  const next = to.params.organizationId
  return typeof next === 'string' && next !== '' && next !== from.params.organizationId
}

/**
 * Before another organization is entered, every form on screen holding changes asks in turn, and
 * one told to stay keeps the page where it is.
 *
 * **The app's first global guard, ahead of the session guard**, which points `X-Org-Id` at the
 * organization being entered and reads its `/access`. A view's own route guards run after every
 * global one: asked there, a form told to stay would sit on the page it was filled in over the next
 * organization's header, and its save would land in that organization. So a form's route guards
 * leave a change of organization to this one.
 */
export function createOrganizationChangeGuard(): NavigationGuardWithThis<undefined> {
  return async (to, from) => {
    if (!entersOtherOrganization(to, from)) return true
    for (const ask of [...questions]) {
      if (!(await ask())) return false
    }
    return true
  }
}

/**
 * **Ask before leaving a form with unsaved changes.** A route change away from the view waits on the
 * answer of the confirmation the view renders (`UnsavedChangesDialog`, bound to `open`, `stay` and
 * `leave`); a page unload gets the browser's own prompt. Idle while `dirty` is false.
 */
export function useUnsavedGuard(dirty: MaybeRefOrGetter<boolean>, options: UnsavedGuardOptions = {}) {
  const open = ref(false)
  let answer: ((leave: boolean) => void) | null = null

  const ask = () => {
    if (!toValue(dirty)) return true
    open.value = true
    return new Promise<boolean>((resolve) => {
      answer = resolve
    })
  }

  // Another organization — whose same page the router reuses, and under which the shell remounts
  // every view — is asked about by the organization change guard, before the tenant moves; this
  // view's own guards ask about everything else.
  onBeforeUnmount(askOnOrganizationChange(ask))
  onBeforeRouteLeave((to, from) => (entersOtherOrganization(to, from) ? true : ask()))

  const params = options.params ?? []
  if (params.length > 0) {
    onBeforeRouteUpdate((to, from) =>
      !entersOtherOrganization(to, from) && params.some((name) => String(to.params[name]) !== String(from.params[name]))
        ? ask()
        : true,
    )
  }

  const settle = (leave: boolean) => {
    open.value = false
    answer?.(leave)
    answer = null
  }
  // Closing the dialog any other way — the scrim, Escape — is staying.
  watch(open, (isOpen) => {
    if (!isOpen && answer) settle(false)
  })

  const beforeUnload = (event: BeforeUnloadEvent) => {
    if (!toValue(dirty)) return
    event.preventDefault()
  }
  window.addEventListener('beforeunload', beforeUnload)
  onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload))

  return { open, stay: () => settle(false), leave: () => settle(true) }
}
